import { Button } from '@/core/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/core/components/ui/card';
import { useState, useRef, useEffect } from 'react';
import { X, Move, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { ActorSelector } from '@/core/components/ActorSelector';
import { appConfig } from '@/core/config';

export function PopupPlayground() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupRoute, setPopupRoute] = useState<string>('');
  const [position, setPosition] = useState({ x: 100, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });

  // Get operator ID from auth store or selected operator
  const authOperatorId = useAuthStore((state) => state.operatorId);
  const selectedOperator = useOperatorStore((state) => state.selectedOperator);
  const operators = useOperatorStore((state) => state.operators);
  const loadOperators = useOperatorStore((state) => state.loadOperators);
  const isLoadingOperators = useOperatorStore((state) => state.isLoading);

  // Local state for manually selected operator ID
  const [manualOperatorId, setManualOperatorId] = useState<string | null>(null);
  const [machineId, setMachineId] = useState<string>('');
  const [manualOperatorIdInput, setManualOperatorIdInput] = useState<string>('');

  // Use manual selection first, then selected operator, finally fallback to auth operator ID
  const operatorId = manualOperatorId || selectedOperator?.operatorId?.toString() || authOperatorId;

  // Load operators on mount
  useEffect(() => {
    if (operators.length === 0 && !isLoadingOperators) {
      loadOperators();
    }
  }, []);

  // Force refresh operators from API (clears cache)
  const handleRefreshOperators = async () => {
    setManualOperatorId(null); // Clear manual selection
    await loadOperators(); // Force reload from API
  };

  const openPopup = (route: string) => {
    setPopupRoute(route);
    setIsPopupOpen(true);
    // Center the popup
    setPosition({
      x: (window.innerWidth - 1200) / 2,
      y: 50
    });
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setPopupRoute('');
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only drag if clicking on the titlebar (not the close button)
    if ((e.target as HTMLElement).closest('button')) return;

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;

    setPosition({
      x: dragRef.current.initialX + deltaX,
      y: dragRef.current.initialY + deltaY
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <>
      {/* In-page Popup Overlay (Production-like Kendo Window) */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-[10003] bg-black/50">
          {/* Popup Window Container - Kendo-style */}
          <div
            className="absolute bg-white shadow-2xl"
            style={{
              top: `${position.y}px`,
              left: `${position.x}px`,
              width: '90%',
              maxWidth: '1200px',
              height: '834px',
              minWidth: '90px',
              minHeight: '50px',
              cursor: isDragging ? 'move' : 'default'
            }}
          >
            {/* Window Titlebar (Kendo k-header style) - Draggable */}
            <div
              className="flex items-center justify-between px-2 py-1 bg-[#3d8bcc] text-white border-b select-none"
              style={{ height: '34px', cursor: 'move' }}
              onMouseDown={handleMouseDown}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Move className="h-3 w-3 opacity-70" />
              </div>
              <button
                onClick={closePopup}
                className="p-1 hover:bg-[#2d7bb8] rounded transition-colors flex items-center justify-center"
                style={{ width: '24px', height: '24px' }}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Window Content */}
            <div className="relative w-full h-full" style={{ height: 'calc(100% - 34px)' }}>
              <iframe
                src={popupRoute}
                className="w-full h-full"
                title="Popup Content"
                style={{ border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Popup Screen Playground</h1>
            <p className="text-muted-foreground mt-2">
              Test how the application behaves when opened in an in-page popup overlay
            </p>
          </div>
        </div>

        {/* Operator Selector Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Operator Selection</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshOperators}
                disabled={isLoadingOperators}
                className="ml-2"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingOperators ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardTitle>
            <CardDescription>Select an operator to use for popup routes that require operator context (searchable)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Selected Operator:</label>
              <ActorSelector
                operators={operators}
                selectedOperatorId={operatorId || ''}
                onOperatorChange={(newOperatorId) => setManualOperatorId(newOperatorId)}
                isLoading={isLoadingOperators}
                disabled={isLoadingOperators}
                className="w-full"
              />
            </div>
            {operatorId && (
              <div className="text-sm text-muted-foreground bg-white p-3 rounded-md border">
                <span className="font-medium">Current Operator ID:</span> {operatorId}
                {manualOperatorId && <span className="ml-2 text-xs">(manually selected)</span>}
                {!manualOperatorId && selectedOperator && <span className="ml-2 text-xs">(from operator store)</span>}
                {!manualOperatorId && !selectedOperator && <span className="ml-2 text-xs">(from auth)</span>}
              </div>
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Machine ID:</label>
              <input
                type="text"
                value={machineId}
                onChange={(e) => setMachineId(e.target.value)}
                placeholder="Enter machine ID"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Operator ID (override for Assign EV):</label>
              <input
                type="text"
                value={manualOperatorIdInput}
                onChange={(e) => setManualOperatorIdInput(e.target.value)}
                placeholder="Enter operator ID (overrides dropdown)"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="text-xs text-muted-foreground bg-blue-100 p-2 rounded-md border border-blue-300">
              💡 <strong>Tip:</strong> If operator names appear outdated after backend updates, click the "Refresh" button to reload fresh data from the API.
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Energy Management</CardTitle>
            <CardDescription>Open the main dashboard in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={() => openPopup('/energy-management')}
              className="w-full"
            >
              Open Dashboard Popup
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Kiosk Sessions</CardTitle>
            <CardDescription>Open kiosk sessions report in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={() => openPopup('/kiosk-reports')}
              className="w-full"
            >
              Open Kiosk Sessions Popup
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sessions Report</CardTitle>
            <CardDescription>Open sessions report in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={() => openPopup('/sessions-report')}
              className="w-full"
            >
              Open Sessions Report Popup
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assign EV</CardTitle>
            <CardDescription>Open EV assignment dashboard in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={() => {
                const resolvedId = manualOperatorIdInput.trim() || operatorId;
                if (resolvedId) {
                  openPopup(`/operator/${resolvedId}/assignEv`);
                }
              }}
              disabled={!manualOperatorIdInput.trim() && !operatorId}
              className="w-full"
            >
              Open Assign EV Popup
            </Button>
            {!manualOperatorIdInput.trim() && !operatorId && (
              <p className="text-xs text-muted-foreground">
                Operator ID not available
              </p>
            )}
            {(manualOperatorIdInput.trim() || operatorId) && (
              <p className="text-xs text-muted-foreground">
                Will open for operator: <span className="font-medium">{manualOperatorIdInput.trim() || operatorId}</span>
                {manualOperatorIdInput.trim() && <span className="ml-1">(manual override)</span>}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assign Kiosk</CardTitle>
            <CardDescription>Open kiosk assignment in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={() => {
                const defaultId = appConfig.getEnvironment() === 'prod' ? '485182134' : '1000403131';
                const id = machineId.trim() || defaultId;
                openPopup(`/machines/${id}/AssignKiosk`);
              }}
              className="w-full"
            >
              Open Assign Kiosk Popup
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Custom URL</CardTitle>
            <CardDescription>Open any custom route in a popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <input
              type="text"
              id="customRoute"
              placeholder="/your-route"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button
              onClick={() => {
                const input = document.getElementById('customRoute') as HTMLInputElement;
                if (input.value) {
                  openPopup(input.value);
                }
              }}
              className="w-full"
            >
              Open Custom Route
            </Button>
          </CardContent>
        </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Production Kendo Window Style</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-sm mb-2">Popup Configuration:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                <li>Kendo UI Window widget style (matches production)</li>
                <li>Modal backdrop (black 50% opacity)</li>
                <li>Fixed positioning with z-index: 10003</li>
                <li>Blue titlebar (#3d8bcc) with close button</li>
                <li>Draggable - click and drag titlebar to move window</li>
                <li>Iframe-based content isolation</li>
                <li>No browser address bar - stays within current page</li>
                <li>Size: ~1200px width × 834px height</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-2">Testing Checklist:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                <li>Verify popup opens as Kendo-style window overlay</li>
                <li>Test drag functionality - click titlebar and move window</li>
                <li>Test authentication flow in iframe context</li>
                <li>Verify iframe can access parent via window.parent</li>
                <li>Test localStorage/sessionStorage sharing between parent and iframe</li>
                <li>Verify operator selection state persists</li>
                <li>Test navigation within popup (should stay in iframe)</li>
                <li>Verify closing popup doesn't reload/affect parent page</li>
                <li>Test responsive behavior and scrolling</li>
              </ul>
            </div>
          </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
